import React from 'react';
import { isNull } from 'lodash';
import { SortDirection, SortOptions } from '@model/common';
import { sortByField } from '@util/common';
import { formatFlightTime } from '@util/date-time';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Theme } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { DestinationLink } from '@components/common/next-link';
import { TripTypes } from '@model/common';
import { NavDestinations, NavDestination } from '@model/content';
import { SelectedOptions, SetOptions } from './NavBar';
import { tripTypeLabels } from '@model/common/tours/trip-types';
import { TripTypesLink } from '@components/trip-types/TripTypesLink';
import { ButtonSize } from '@components/material-ui';
import {
  DestinationsContainer,
  FiltersColumn,
  FiltersHeading,
  FilterItemsWrapper,
  FilterItem,
  DestinationsContentContainer,
  DestinationsSortBar,
  StyledButton,
  Sort,
  SortHeading,
  SortOptionsContainer,
  SortOption,
  DestinationsListContainer,
  AdditionalSortingLabel,
  DestinationItem,
  DestinationItemLabelWrapper,
  FlagIcon,
  ViewAllTripTypeWrapper
} from './styles/DestinationsNavContent';

/* ***************** *
 *       Types       *
 * ***************** */
export interface DestinationsNavContentProps {
  theme?: Theme;
  testId?: string;
  selectedOptions: SelectedOptions;
  setOptions: SetOptions;
  onToggleDesktopNav?: () => void;
  destinations: NavDestinations | null;
}

/* ************************************* *
 *    DestinationsNavContentComponent    *
 * ************************************* */

export const DestinationsNavContentComponent = (props: DestinationsNavContentProps) => {
  const t: any = useI18NextContext();
  const {
    testId,
    selectedOptions: { selectedFilter, selectedSortOption },
    setOptions: { setSelectedFilter, setSelectedSortOption },
    onToggleDesktopNav,
    destinations
  } = props;

  if (isNull(destinations)) {
    return null;
  }

  /* *** LOGIC *** */
  const viewAllLabel = t('trip-types__view--all', { tripType: tripTypeLabels[selectedFilter] });

  const sortDestinations = (destinations: Array<NavDestination>) => {
    // sort by selected sort option
    let fieldName;

    switch (selectedSortOption) {
      case SortOptions.SORT_ALPHABETICAL:
        fieldName = 'name';
        break;
      case SortOptions.SORT_BY_FLIGHT_TIMES:
        fieldName = 'flightTime';
        break;
      default: {
        fieldName = 'name';
      }
    }

    return sortByField(destinations, fieldName, SortDirection.ASC);
  };

  const getAdditionalSortingLabel = ({ flightTime }: NavDestination) => {
    switch (selectedSortOption) {
      case SortOptions.SORT_BY_FLIGHT_TIMES: {
        return (
          <AdditionalSortingLabel data-testid={TestId.destinationsNavContent.additionalSortLabel}>
            {formatFlightTime(flightTime)}
          </AdditionalSortingLabel>
        );
      }
      default: {
        return '';
      }
    }
  };

  const handleSetSelectedFilter = (tripType: TripTypes) => {
    setSelectedFilter(tripType);
  };

  /* *** RENDERERS *** */
  const renderFilters = () =>
    Object.keys(destinations).map((tripTypeId, index: number) => {
      return (
        <FilterItem
          data-testid={TestId.destinationsNavContent.filterItem}
          key={`${tripTypeId}-${index}`}
          isSelected={selectedFilter === tripTypeId}
          onClick={() => handleSetSelectedFilter(tripTypeId as TripTypes)}
        >
          {t(tripTypeId)}
        </FilterItem>
      );
    });

  const renderSortOptions = () => {
    const sortOptions: Array<SortOptions> = [SortOptions.SORT_ALPHABETICAL, SortOptions.SORT_BY_FLIGHT_TIMES];

    return sortOptions.map((sortOption: SortOptions, index: number) => (
      <SortOption
        data-testid={TestId.destinationsNavContent.sortOption}
        key={`${sortOption}-${index}`}
        onClick={() => setSelectedSortOption(sortOption)}
        isSelected={selectedSortOption === sortOption}
      >
        {t(sortOption)}
      </SortOption>
    ));
  };

  const renderDestinationsLists = (navDestinations: NavDestinations) => {
    return Object.entries(navDestinations).map(([tripType, { destinations }]) => {
      return renderDestinationsList(tripType, destinations);
    });
  };

  const renderDestinationsList = (tripType, destinationList: Array<NavDestination>) => {
    const destinationItems = sortDestinations(destinationList).map((item: NavDestination, index: number) => {
      const { path, name, isoCode } = item;

      return (
        <DestinationItem
          key={`${path}-${index}`}
          data-testid={TestId.destinationsNavContent.destinationsItem}
          onClick={onToggleDesktopNav}
        >
          <DestinationLink path={path} tripType={selectedFilter}>
            <>
              <FlagIcon countryCode={isoCode} />
              <DestinationItemLabelWrapper>
                {name}
                {getAdditionalSortingLabel(item)}
              </DestinationItemLabelWrapper>
            </>
          </DestinationLink>
        </DestinationItem>
      );
    });

    return (
      <DestinationsListContainer
        key={tripType}
        isSelected={tripType === selectedFilter}
        data-testid={TestId.destinationsNavContent.destinationsListContainer}
        displayInColumns={destinationList.length > 4}
      >
        {destinationItems}
        <TripTypesLink tripTypeId={selectedFilter}>
          <ViewAllTripTypeWrapper onClick={onToggleDesktopNav}>{viewAllLabel}</ViewAllTripTypeWrapper>
        </TripTypesLink>
      </DestinationsListContainer>
    );
  };

  return (
    <DestinationsContainer data-testid={testId || TestId.destinationsNavContent.main}>
      <FiltersColumn data-testid={TestId.destinationsNavContent.tripTypesColumn}>
        <FiltersHeading data-testid={TestId.destinationsNavContent.tripTypesHeading}>
          {t('nav__filter--heading')}
        </FiltersHeading>
        <FilterItemsWrapper>{renderFilters()}</FilterItemsWrapper>
      </FiltersColumn>
      <DestinationsContentContainer data-testid={TestId.destinationsNavContent.destinationsContentContainer}>
        <DestinationsSortBar data-testid={TestId.destinationsNavContent.sortBar}>
          <TripTypesLink tripTypeId={selectedFilter}>
            <StyledButton label={viewAllLabel} size={ButtonSize.SMALL} onClick={onToggleDesktopNav} />
          </TripTypesLink>
          <Sort>
            <SortHeading data-testid={TestId.destinationsNavContent.sortHeading}>
              {t('common__label--sort-by')}
            </SortHeading>
            <SortOptionsContainer data-testid={TestId.destinationsNavContent.sortOptionsContainer}>
              {renderSortOptions()}
            </SortOptionsContainer>
          </Sort>
        </DestinationsSortBar>
        {destinations && renderDestinationsLists(destinations)}
      </DestinationsContentContainer>
    </DestinationsContainer>
  );
};

export const DestinationsNavContent = withTheme(DestinationsNavContentComponent);
